import type { NewsItem } from '~/utils/news'
import { getAllNews, getNewsBySlug, searchNews } from '~/utils/news'
import { NEWS_SOURCE } from '~/utils/newsSource'

type NewsListResponse = {
  ok: boolean
  list: NewsItem[]
  total?: number
  page?: number
  pageSize?: number
}

type NewsDetailResponse = {
  ok: boolean
  item: NewsItem
}

type NewsListPagerState = {
  q: string
  list: NewsItem[]
  total: number
  page: number
  scrollY: number
}

const NEWS_LIST_PAGER_KEY = 'public-news-list-pager'
const NEWS_LIST_PRESERVE_KEY = 'public-news-list-preserve-once'

function useNewsListPager() {
  return useState<NewsListPagerState>(NEWS_LIST_PAGER_KEY, () => ({
    q: '',
    list: [],
    total: 0,
    page: 0,
    scrollY: 0,
  }))
}

function useNewsListPreserveOnce() {
  return useState(NEWS_LIST_PRESERVE_KEY, () => false)
}

function isNewsDetailPath(path: string) {
  return /^(\/[a-z]{2})?\/news\/[^/]+\/?$/.test(path)
}

/** Homepage strip / list page — switches by NEWS_SOURCE. */
export async function usePublicNewsList(options?: {
  q?: MaybeRefOrGetter<string>
  /** Non-paginated: fetch first N (homepage). */
  limit?: number
  /** Paginated list page size (default 20). */
  pageSize?: number
  /** Accumulate pages via loadMore. */
  paginated?: boolean
}) {
  const q = computed(() => String(toValue(options?.q) || '').trim())
  const paginated = Boolean(options?.paginated)
  const pageSize = paginated ? options?.pageSize ?? 20 : options?.limit ?? 50

  if (NEWS_SOURCE === 'static') {
    if (!paginated) {
      const list = computed(() => {
        const items = q.value ? searchNews(q.value) : getAllNews()
        return items.slice(0, pageSize)
      })
      return {
        list,
        total: computed(() => list.value.length),
        pending: ref(false),
        error: ref(null),
        hasMore: computed(() => false),
        loadingMore: ref(false),
        loadMore: async () => {},
        saveScroll: () => {},
        restoreScroll: () => {},
        onListLeave: () => {},
        onListEnter: async () => {},
        markPreserveForDetail: () => {},
      }
    }

    const pager = useNewsListPager()
    const preserveOnce = useNewsListPreserveOnce()
    const pending = ref(false)
    const loadingMore = ref(false)

    function syncStatic(reset: boolean) {
      const items = q.value ? searchNews(q.value) : getAllNews()
      const nextPage = reset ? 1 : Math.max(1, pager.value.page)
      pager.value = {
        q: q.value,
        total: items.length,
        page: nextPage,
        list: items.slice(0, nextPage * pageSize),
        scrollY: reset ? 0 : pager.value.scrollY,
      }
    }

    if (preserveOnce.value && pager.value.q === q.value && pager.value.page >= 1 && pager.value.list.length) {
      preserveOnce.value = false
    } else {
      preserveOnce.value = false
      syncStatic(true)
    }

    watch(q, (next, prev) => {
      if (next === prev) return
      preserveOnce.value = false
      syncStatic(true)
    })

    const hasMore = computed(() => pager.value.list.length < pager.value.total)

    async function loadMore() {
      if (!hasMore.value || loadingMore.value) return
      loadingMore.value = true
      try {
        pager.value.page += 1
        syncStatic(false)
      } finally {
        loadingMore.value = false
      }
    }

    function saveScroll() {
      if (!import.meta.client) return
      pager.value.scrollY = window.scrollY
    }

    function restoreScroll() {
      if (!import.meta.client) return
      const y = pager.value.scrollY
      nextTick(() => {
        requestAnimationFrame(() => {
          window.scrollTo({ top: y, left: 0, behavior: 'instant' as ScrollBehavior })
        })
      })
    }

    function onListLeave(toPath: string) {
      saveScroll()
      preserveOnce.value = isNewsDetailPath(toPath)
      if (!preserveOnce.value) {
        pager.value = { q: q.value, list: [], total: 0, page: 0, scrollY: 0 }
      }
    }

    async function onListEnter() {
      if (preserveOnce.value && pager.value.q === q.value && pager.value.page >= 1 && pager.value.list.length) {
        preserveOnce.value = false
        restoreScroll()
        return
      }
      preserveOnce.value = false
      syncStatic(true)
      if (import.meta.client) {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior })
      }
    }

    return {
      list: computed(() => pager.value.list),
      total: computed(() => pager.value.total),
      pending,
      error: ref(null),
      hasMore,
      loadingMore,
      loadMore,
      saveScroll,
      restoreScroll,
      onListLeave,
      onListEnter,
      markPreserveForDetail: () => {
        saveScroll()
        preserveOnce.value = true
      },
    }
  }

  if (!paginated) {
    const { data, pending, error } = await useFetch<NewsListResponse>('/api/news', {
      query: computed(() => ({
        q: q.value || undefined,
        limit: pageSize,
        page: 1,
        pageSize,
      })),
      watch: [q],
    })

    const list = computed(() => data.value?.list ?? [])
    return {
      list,
      total: computed(() => data.value?.total ?? list.value.length),
      pending,
      error,
      hasMore: computed(() => false),
      loadingMore: ref(false),
      loadMore: async () => {},
      saveScroll: () => {},
      restoreScroll: () => {},
      onListLeave: () => {},
      onListEnter: async () => {},
      markPreserveForDetail: () => {},
    }
  }

  // Paginated API list — keep state only when returning from a detail page
  const pager = useNewsListPager()
  const preserveOnce = useNewsListPreserveOnce()
  const loadingMore = ref(false)
  const pending = ref(false)
  const error = ref<Error | null>(null)

  async function fetchPage(pageNum: number) {
    return await $fetch<NewsListResponse>('/api/news', {
      query: {
        q: q.value || undefined,
        page: pageNum,
        pageSize,
      },
    })
  }

  async function loadFirstPage(force = false) {
    if (!force && pager.value.q === q.value && pager.value.page >= 1 && pager.value.list.length) {
      return
    }
    pending.value = true
    error.value = null
    try {
      const res = await fetchPage(1)
      pager.value = {
        q: q.value,
        list: res.list || [],
        total: Number(res.total || 0),
        page: 1,
        scrollY: 0,
      }
    } catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err))
      if (force || pager.value.q !== q.value) {
        pager.value = { q: q.value, list: [], total: 0, page: 0, scrollY: 0 }
      }
    } finally {
      pending.value = false
    }
  }

  // Fresh entry / browser refresh → page 1.
  // Returning from detail uses preserveOnce + cached pager (handled in onListEnter).
  if (preserveOnce.value && pager.value.q === q.value && pager.value.page >= 1 && pager.value.list.length) {
    preserveOnce.value = false
    if (import.meta.client) {
      nextTick(() => restoreScroll())
    }
  } else {
    preserveOnce.value = false
    await loadFirstPage(true)
  }

  watch(q, async (next, prev) => {
    if (next === prev) return
    preserveOnce.value = false
    await loadFirstPage(true)
  })

  const hasMore = computed(() => pager.value.list.length < pager.value.total)

  async function loadMore() {
    if (!hasMore.value || loadingMore.value || pending.value) return
    loadingMore.value = true
    error.value = null
    try {
      const nextPage = pager.value.page + 1
      const res = await fetchPage(nextPage)
      pager.value = {
        ...pager.value,
        list: [...pager.value.list, ...(res.list || [])],
        total: Number(res.total || 0),
        page: nextPage,
      }
    } catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err))
    } finally {
      loadingMore.value = false
    }
  }

  function saveScroll() {
    if (!import.meta.client) return
    pager.value.scrollY = window.scrollY
  }

  function restoreScroll() {
    if (!import.meta.client) return
    const y = pager.value.scrollY
    nextTick(() => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: y, left: 0, behavior: 'instant' as ScrollBehavior })
      })
    })
  }

  /** Call when leaving list: preserve only if navigating to a news detail. */
  function onListLeave(toPath: string) {
    saveScroll()
    preserveOnce.value = isNewsDetailPath(toPath)
    if (!preserveOnce.value) {
      // Leaving to other pages — next list visit starts fresh
      pager.value = { q: q.value, list: [], total: 0, page: 0, scrollY: 0 }
    }
  }

  /** Call when list becomes active again (keepalive). */
  async function onListEnter() {
    if (preserveOnce.value && pager.value.q === q.value && pager.value.page >= 1 && pager.value.list.length) {
      preserveOnce.value = false
      restoreScroll()
      return
    }
    preserveOnce.value = false
    await loadFirstPage(true)
    if (import.meta.client) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior })
    }
  }

  function markPreserveForDetail() {
    saveScroll()
    preserveOnce.value = true
  }

  return {
    list: computed(() => pager.value.list),
    total: computed(() => pager.value.total),
    pending,
    error,
    hasMore,
    loadingMore,
    loadMore,
    saveScroll,
    restoreScroll,
    onListLeave,
    onListEnter,
    markPreserveForDetail,
  }
}

/** News detail — switches by NEWS_SOURCE. */
export async function usePublicNewsArticle(slug: string) {
  if (NEWS_SOURCE === 'static') {
    return {
      article: computed(() => getNewsBySlug(slug) ?? null),
      pending: ref(false),
      error: ref(null),
    }
  }

  const { data, pending, error } = await useAsyncData(`public-news-${slug}`, async () => {
    try {
      return await $fetch<NewsDetailResponse>(`/api/news/${encodeURIComponent(slug)}`)
    } catch {
      return null
    }
  })

  const article = computed(() => data.value?.item ?? null)
  return { article, pending, error }
}
